import {Alert} from 'src/design/atoms/Alert';
import {Stack} from 'src/design/atoms/Stack';
import {useEffect} from 'react';
import {useParams} from 'react-router-dom';
import {FeatureFlagEnum} from 'src/api/ProactApiClient';
import {useGetCourseAssignmentActiveSessionQuery} from 'src/redux/queries/courseAssignment';
import {Spinner} from 'src/design/atoms/Spinner';
import {BackBar} from 'src/app/layout/BackBar';
import {ContentWrapper} from 'src/app/layout/ContentWrapper';
import {PageHeader} from 'src/design/organisms/PageHeader';
import {routeRegistry} from 'src/pages/RouteRegistry';
import {PathUtility} from 'src/shared/constants/PathBuilder';
import {useAlert} from 'src/shared/hooks/useAlert';
import useSetDocumentTitle from 'src/shared/hooks/useSetDocumentTitle';
import {useTrainingBreadcrumbs} from 'src/shared/hooks/useTrainingBreadcrumbs';
import {parseErrorMessage} from 'src/shared/utils/parseErrorMessage';
import {CourseAssignmentOverviewContent} from 'src/app/training/CourseAssignmentOverviewContent';
import {
  ClassroomOnlyPill,
  CourseAssignmentPillConfig,
} from 'src/app/training/courseAssignmentPillConfig';
import {TrainingDocumentTitle} from '../../../../app/training/courseContentValidation';
import {TrainingIcon} from '../../../../app/training/coursePills';

export const CourseAssignmentOverviewPage = () => {
  useSetDocumentTitle(TrainingDocumentTitle);
  const {assignmentId} = useParams();

  const {setAlert} = useAlert();

  const {
    data: tracker,
    isLoading,
    error,
  } = useGetCourseAssignmentActiveSessionQuery(parseInt(assignmentId!), {
    skip: !assignmentId,
    refetchOnMountOrArgChange: true,
  });

  useEffect(() => {
    if (error) {
      setAlert(parseErrorMessage(error), 'error');
    }
  }, [error]);

  const {breadcrumbs, fallbackTo, fallbackLabel} = useTrainingBreadcrumbs({
    mode: 'user-course',
    courseName: tracker?.courseDefinition.name,
  });

  const pills = [
    ...CourseAssignmentPillConfig(
      tracker?.assignment?.state,
      undefined,
      undefined,
      tracker?.assignment?.dueAt
    ),
    ...(tracker?.assignment?.isClassroomOnly ? [ClassroomOnlyPill] : []),
  ];

  return (
    <ContentWrapper featureFlag={FeatureFlagEnum.Training}>
      <BackBar
        fallbackTo={fallbackTo}
        fallbackLabel={fallbackLabel}
        breadcrumbs={breadcrumbs}
      />
      {isLoading ? (
        <Spinner loadingText="Loading...." />
      ) : error || !tracker ? (
        <Alert color="red" title="Error">
          Failed to load course session. Please try again later.
        </Alert>
      ) : (
        <Stack>
          <PageHeader>
            <PageHeader.Title icon={TrainingIcon}>
              Course {tracker.courseDefinition.name}
            </PageHeader.Title>
            {tracker.courseDefinition?.internalIdentifier && (
              <PageHeader.Meta>
                {tracker.courseDefinition.internalIdentifier}
              </PageHeader.Meta>
            )}
            {pills.length > 0 && (
              <PageHeader.Badges>
                {pills.map((pill, index) => (
                  <PageHeader.Badge
                    key={index}
                    color={pill.color}
                    variant="light">
                    {pill.text}
                  </PageHeader.Badge>
                ))}
              </PageHeader.Badges>
            )}
          </PageHeader>

          <CourseAssignmentOverviewContent tracker={tracker} />
        </Stack>
      )}
    </ContentWrapper>
  );
};

// Register the route with the new system
routeRegistry.registerPageRoute({
  backNavigation: 'page',
  path: PathUtility.TrainingCourseOverview(':assignmentId'),
  element: <CourseAssignmentOverviewPage />,
  isPrivate: true,
});

export default CourseAssignmentOverviewPage;
